/** Two-line property address: street address (primary) over a city/state subtitle (secondary), xs typography, snug line height.
 *  NO client directive: it holds no state and no handlers, and the explore page's server-rendered preview card renders
 *  it beside the interactive card, so the two address blocks are one component rather than a copy. */

import * as React from 'react';
import { FLEX_FILL_TRUNCATE_STYLE } from '@federa/const/style-flex-presets';
import { MUTED_LABEL_SECONDARY, TEXT_XS_MEDIUM } from '../common/layout-styles.ts';
import { FLEX_COLUMN_GAP2_STYLE, getLineClampStyles } from '../common/style-primitives.ts';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';

interface PropertyAddressBlockProps {
	/** Street address text */
	address: string;
	/** City name */
	city: string;
	/** State abbreviation or name */
	state: string;
	style?: React.CSSProperties;
}

const ROOT_STYLES: React.CSSProperties = {
	...FLEX_FILL_TRUNCATE_STYLE,
	...FLEX_COLUMN_GAP2_STYLE,
};

const ADDRESS_STYLES: React.CSSProperties = {
	...TEXT_XS_MEDIUM,
	color: colors.text.primary,
	lineHeight: typography.lineHeight.snug,
	whiteSpace: 'normal',
	wordBreak: 'break-word',
	...getLineClampStyles(2),
};

const SUBTITLE_STYLES: React.CSSProperties = {
	...MUTED_LABEL_SECONDARY,
	fontWeight: typography.fontWeight.normal,
	lineHeight: typography.lineHeight.snug,
	whiteSpace: 'normal',
	wordBreak: 'break-word',
};

export function PropertyAddressBlock({ address, city, state, style }: PropertyAddressBlockProps) {
	const hasCityState = city && state;
	const mergedStyles = style ? { ...ROOT_STYLES, ...style } : ROOT_STYLES;

	return (
		<div style={mergedStyles}>
			<div style={ADDRESS_STYLES}>{address}</div>
			{hasCityState && (
				<div style={SUBTITLE_STYLES}>
					{city}, {state}
				</div>
			)}
		</div>
	);
}
